import { useState } from "react";
import { useTranslation } from "react-i18next";
import UniversityAdminLayout from "../../layouts/UniversityAdminLayout";
import {
  Search,
  Download,
  Plus,
  ChevronDown,
  SlidersHorizontal,
  TrendingUp,
  Edit,
  Ban,
  Check,
  X,
} from "lucide-react";
import { createUniversityStudent } from "../../api/services/universityAdminService";
import { useToast } from "../../components/ToastContext";
import DatePicker from "../../components/common/DatePicker";
import "../../assets/styles/colors.css";

export default function StudentManagement() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    dob: "",
    organization_name: "",
  });

  // Dummy student data
  const students = [
    {
      id: 1,
      name: "Felix Chen",
      email: "felix.c@univ.edu",
      initials: "FC",
      department: "Cyber Security",
      year: "Junior",
      flagsCaptured: 2450,
      status: "Active",
    },
    {
      id: 2,
      name: "Sarah Jenkins",
      email: "s.jenkins@univ.edu",
      initials: "SJ",
      department: "Computer Science",
      year: "Senior",
      flagsCaptured: 2120,
      status: "Active",
    },
    {
      id: 3,
      name: "Marcus Thorne",
      email: "m.thorne@univ.edu",
      initials: "MT",
      department: "Information Systems",
      year: "Sophomore",
      flagsCaptured: 1980,
      status: "Inactive",
    },
    {
      id: 4,
      name: "Aisha Omar",
      email: "aisha.o@univ.edu",
      initials: "AO",
      department: "Cyber Security",
      year: "Freshman",
      flagsCaptured: 840,
      status: "Active",
    },
    {
      id: 5,
      name: "Marcus Thorne",
      email: "m.thorne@univ.edu",
      initials: "MT",
      department: "Information Systems",
      year: "Sophomore",
      flagsCaptured: 1980,
      status: "Inactive",
    },
    {
      id: 6,
      name: "Marcus Thorne",
      email: "m.thorne@univ.edu",
      initials: "MT",
      department: "Information Systems",
      year: "Sophomore",
      flagsCaptured: 1980,
      status: "Inactive",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createUniversityStudent(formData);

      // Success - close modal and reset form
      setIsAddStudentOpen(false);
      setFormData({
        full_name: "",
        username: "",
        email: "",
        dob: "",
        organization_name: "",
      });

      // Show success notification
      showToast(
        "Student created successfully! An email has been sent to them for setting their password.",
        "success",
        5000,
      );

      // TODO: Refresh student list or add to local state
    } catch (error) {
      console.error("Error creating student:", error);
      setSubmitError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to create student. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UniversityAdminLayout>
      <div className="px-8 py-8 space-y-6">
        <div>
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-(--color-text-primary)">
                Student Management
              </h1>
              <p className="text-xs mt-0.5 text-(--color-text-muted)">
                Manage and monitor student progress across all departments.
              </p>
            </div>
            <div className="flex gap-3">
              <button className="flex items-center gap-2 px-4 py-2 text-(--color-text-primary) bg-(--color-bg-card) border border-(--color-border) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm">
                <Download className="w-4 h-4" />
                Bulk Import (CSV)
              </button>
              <button
                onClick={() => setIsAddStudentOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-white dark:text-black bg-(--color-primary) rounded-lg hover:bg-(--color-primary)/90 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Single Student
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl bg-(--color-bg-card)">
            <div className="flex items-center gap-4 px-5 py-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-(--color-text-muted)" />
                <input
                  type="text"
                  placeholder="Search students..."
                  className="w-full pl-10 pr-4 py-2 border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary) text-sm"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-2 border border-(--color-border) rounded-lg text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors text-sm">
                All Departments
                <ChevronDown className="w-4 h-4" />
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-(--color-border) rounded-lg text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors text-sm">
                All Years
                <ChevronDown className="w-4 h-4" />
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-(--color-border) rounded-lg text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors text-sm">
                <SlidersHorizontal className="w-4 h-4" />
                More Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-xl bg-(--color-bg-card) border border-(--color-border) overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-(--color-text-primary) border-b border-(--color-border)">
                    <th className="px-5 py-3 text-left font-bold">
                      Student Name
                    </th>
                    <th className="px-5 py-3 text-left font-bold">
                      Department
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Year</th>
                    <th className="px-5 py-3 text-left font-bold">
                      Flags Captured
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Status</th>
                    <th className="px-5 py-3 text-left font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr
                      key={student.id}
                      className="border-t border-(--color-border) hover:bg-(--color-bg-overlay) dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-(--color-primary)/20 flex items-center justify-center text-xs font-bold text-(--color-primary)">
                            {student.initials}
                          </span>
                          <div>
                            <div className="font-medium text-(--color-text-primary)">
                              {student.name}
                            </div>
                            <div className="text-xs text-(--color-text-muted)">
                              {student.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-(--color-text-primary)">
                        {student.department}
                      </td>
                      <td className="px-5 py-3 text-(--color-text-primary)">
                        {student.year}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1 text-(--color-text-primary) font-medium">
                          {student.flagsCaptured.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            student.status === "Active"
                              ? "bg-green-500/15 text-green-400 border border-green-400/30"
                              : "bg-gray-500/15 text-gray-400 border border-gray-400/30"
                          }`}
                        >
                          {student.status}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-1.5 text-(--color-text-muted) hover:text-blue-400 transition-colors"
                            title="View Stats"
                          >
                            <TrendingUp className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 text-(--color-text-muted) hover:text-green-400 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 text-(--color-text-muted) hover:text-red-400 transition-colors"
                            title="Disable"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-5 py-4 border-t border-(--color-border) flex items-center justify-between">
              <div className="text-xs text-(--color-text-muted)">
                Showing 1 to 6 of 128 students
              </div>
              <div className="flex gap-2">
                <button className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs">
                  Previous
                </button>
                <button className="px-3 py-1 bg-(--color-primary) text-white rounded text-xs">
                  1
                </button>
                <button className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Student Modal */}
      {isAddStudentOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-(--color-bg-card) rounded-xl shadow-xl max-w-2xl w-full border border-(--color-border)">
            <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
              <h2 className="text-lg font-semibold text-(--color-text-primary)">
                Add Single Student
              </h2>
              <button
                onClick={() => {
                  setIsAddStudentOpen(false);
                  setSubmitError(null);
                }}
                className="text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-(--color-text-primary) mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary) text-sm"
                    placeholder="John Student"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-(--color-text-primary) mb-1.5">
                    Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary) text-sm"
                    placeholder="john_student"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-(--color-text-primary) mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary) text-sm"
                    placeholder="john@university.edu"
                    required
                  />
                </div>

                <div>
                  <DatePicker
                    label="Date of Birth"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    placeholder="Select date of birth"
                    required
                    margin="mb-0"
                    size="small"
                    maxYear={new Date().getFullYear() - 13}
                    minYear={new Date().getFullYear() - 100}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-(--color-text-primary) mb-1.5">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    name="organization_name"
                    value={formData.organization_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary) text-sm"
                    placeholder="State University"
                    required
                  />
                </div>
              </div>

              {submitError && (
                <div className="col-span-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {submitError}
                </div>
              )}

              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddStudentOpen(false);
                    setSubmitError(null);
                  }}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-(--color-primary) text-white rounded-lg hover:bg-(--color-primary)/90 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Add Student
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </UniversityAdminLayout>
  );
}
